import React, { useEffect, useMemo, useState } from "react";

function ImageGrid({ images }) {
  const list = useMemo(() => {
    const raw = Array.isArray(images) ? images.filter(Boolean) : [];
    return raw
      .map((img, idx) => {
        const url = typeof img === "string" ? img : img?.url;
        const alt = typeof img === "string" ? `Image ${idx + 1}` : img?.alt || `Image ${idx + 1}`;
        return url ? { url, alt } : null;
      })
      .filter(Boolean);
  }, [images]);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const openAt = (idx) => {
    setActive(idx);
    setOpen(true);
  };

  const close = () => setOpen(false);

  const prev = () => setActive((a) => (a - 1 + list.length) % list.length);
  const next = () => setActive((a) => (a + 1) % list.length);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list.length]);

  if (list.length === 0) return null;

  const current = list[active];

  return (
    <>
      {/* Tighter thumbnails */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {list.map((img, idx) => (
          <button
            key={`${img.url}-${idx}`}
            type="button"
            onClick={() => openAt(idx)}
            className="border rounded-lg overflow-hidden bg-white hover:shadow-sm transition text-left"
            aria-label={`Open image ${idx + 1}`}
          >
            <div className="relative w-full aspect-[4/3] bg-secondary">
              <img src={img.url} alt={img.alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {open ? (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            // click backdrop to close (but not clicks inside the panel)
            if (e.target === e.currentTarget) close();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-6xl">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <div className="flex items-center justify-between p-3 border-b">
                <div className="text-sm opacity-80">
                  {active + 1} / {list.length}
                </div>

                <div className="flex gap-2">
                  <button className="border px-3 py-2 rounded" onClick={prev} type="button">
                    ← Prev
                  </button>
                  <button className="border px-3 py-2 rounded" onClick={next} type="button">
                    Next →
                  </button>
                  <button className="bg-red-500 text-white px-3 py-2 rounded" onClick={close} type="button">
                    Close
                  </button>
                </div>
              </div>

              <div className="bg-black">
                <div className="relative w-full" style={{ height: "min(75vh, 720px)" }}>
                  {current ? (
                    <img
                      src={current.url}
                      alt={current.alt}
                      className="absolute inset-0 w-full h-full object-contain bg-black"
                    />
                  ) : null}
                </div>
              </div>

              <div className="p-3 text-sm opacity-80 truncate">
                {current?.alt || ""}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default ImageGrid;
